import { useCallback, useEffect, useState } from "react";
import { useParams } from "wouter";
import { Loader2 } from "lucide-react";
import { api, type AiStockAnalyzedItem } from "@/lib/api";
import { aiStockUserMessage } from "@/lib/aiStockUserMessage";
import { AiStockCameraCapture } from "@/components/estoque/AiStockCameraCapture";
import {
  AiStockQrStatusScreen,
  qrSessionStatusCopy,
} from "@/components/estoque/AiStockQrStatusScreen";
import { applyClientTheme } from "@/lib/clientConfig";

/**
 * Página pública do QR: captura sem login no ERP.
 * Rota: /estoque/ai-captura/:token
 */
export default function AiStockMobileCapturePage() {
  const params = useParams<{ token: string }>();
  const token = params.token ? decodeURIComponent(params.token) : "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<string | null>(null);
  const [canUpload, setCanUpload] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setError("Link inválido.");
        setSessionStatus(null);
        setLoading(false);
        return;
      }
      try {
        const info = await api.aiStockMobileInfo(token);
        if (cancelled) return;
        setSessionStatus(info.status || null);
        applyClientTheme({
          client_id: "qr",
          name: info.name || "GrandSystem",
          fantasy_name: info.name || "GrandSystem",
          nome_empresa: info.name || "GrandSystem",
          primary_color: info.primary_color || "#E85D04",
          secondary_color: "#00000022",
          logo_url: "",
          menu_logo_url: "",
          login_logo_url: "",
          nf_logo_url: "",
          background_logo_url: "",
          cnpj: "",
          ie: "",
          im: "",
          address: "",
          number: "",
          complement: "",
          district: "",
          city: "",
          uf: "",
          cep: "",
          phone: "",
          base_url: "",
          modo_atendimento: "mesa",
          receipt: {
            legal_name: "",
            fantasy_name: "",
            cnpj: "",
            ie: "",
            im: "",
            address: "",
            number: "",
            complement: "",
            district: "",
            city: "",
            uf: "",
            cep: "",
            phone: "",
            logo_url: "",
          },
        });
        if (!info.can_upload) {
          setError(info.error || "Esta sessão não está disponível.");
          setCanUpload(false);
        } else {
          setCanUpload(true);
        }
      } catch (e) {
        if (!cancelled) {
          setError(aiStockUserMessage(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onResult = useCallback((_itens: AiStockAnalyzedItem[]) => {
    setDone(true);
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !canUpload) {
    const copy = qrSessionStatusCopy(sessionStatus, error);
    return <AiStockQrStatusScreen title={copy.title} primary={copy.primary} secondary={copy.secondary} />;
  }

  if (done) {
    return (
      <AiStockQrStatusScreen
        title="Imagem enviada"
        primary={
          <>
            A leitura continua no computador.
            <br />
            Você já pode fechar esta tela.
          </>
        }
        secondary="Seu envio foi concluído com sucesso."
        successBadge
      />
    );
  }

  return (
    <AiStockCameraCapture mode="token" token={token} onResult={onResult} />
  );
}
